import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { confirmDialog } from '@/lib/confirmDialog'
import { useTeam } from '@/hooks/useTeam'
import { useNavigate } from 'react-router-dom'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import Input from '@/components/common/Input'
import Select from '@/components/common/Select'
import Textarea from '@/components/common/Textarea'
import Loading from '@/components/common/Loading'
import WikiViewToggle from '@/components/wiki/WikiViewToggle'
import WikiDocumentView from '@/components/wiki/WikiDocumentView'
import WikiVersionHistory from '@/components/wiki/WikiVersionHistory'
import WikiRecentChanges from '@/components/wiki/WikiRecentChanges'

function WikiManager() {
  const { team, loading: teamLoading } = useTeam()
  const navigate = useNavigate()
  
  const [wikiItems, setWikiItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'note',
    section: 'Quick Notes',
    parent_id: null,
    allow_ai_override: false
  })
  const [parentItemsInSection, setParentItemsInSection] = useState([])
  const [saving, setSaving] = useState(false)
  const [viewMode, setViewMode] = useState('document')
  const [searchQuery, setSearchQuery] = useState('')
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const [pendingSuggestions, setPendingSuggestions] = useState([])
  const [showSuggestionsBanner, setShowSuggestionsBanner] = useState(false)
  
  // New state for auto-update notifications
  const [recentAutoUpdates, setRecentAutoUpdates] = useState([])
  const [showAutoUpdateNotice, setShowAutoUpdateNotice] = useState(false)
  const [showRecentChanges, setShowRecentChanges] = useState(false)


  useEffect(() => {
    if (team?.id) {
      fetchWikiItems()
      fetchPendingSuggestions()
      fetchRecentAutoUpdates()
    }
  }, [team?.id])

  const fetchWikiItems = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('wiki_items')
        .select('*')
        .eq('team_id', team.id)
        .order('is_pinned', { ascending: false })
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false })

      if (error) throw error

      setWikiItems(data || [])
    } catch (err) {
    } finally {
      setLoading(false)
    }
  }

  const fetchPendingSuggestions = async () => {
    try {
      const { data, error } = await supabase
        .from('wiki_suggestions')
        .select('*')
        .eq('team_id', team.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
      
      if (error) throw error
      
      setPendingSuggestions(data || [])
      setShowSuggestionsBanner((data || []).length > 0)
    } catch (err) {
      console.error('Failed to fetch pending suggestions:', err)
    }
  }

  const fetchRecentAutoUpdates = async () => {
    try {
      // Get auto-applied changes from last 24 hours
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
      
      const { data, error } = await supabase
        .from('wiki_change_log')
        .select('id, operation, new_data, created_at')
        .eq('team_id', team.id)
        .eq('auto_applied', true)
        .is('undone_at', null)
        .gte('created_at', oneDayAgo)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      
      setRecentAutoUpdates(data || [])
      setShowAutoUpdateNotice((data || []).length > 0)
    } catch (err) {
      console.error('Failed to fetch recent auto updates:', err)
    }
  }

  const handleDismissAll = async () => {
    const confirmed = await confirmDialog({
      title: 'Dismiss All Suggestions?',
      message: 'This will permanently remove all pending AI suggestions. You cannot undo this action.',
      confirmText: 'Dismiss All',
      confirmVariant: 'danger'
    })
    
    if (!confirmed) return
    
    try {
      const { error } = await supabase
        .from('wiki_suggestions')
        .update({ 
          status: 'dismissed',
          reviewed_at: new Date().toISOString()
        })
        .eq('team_id', team.id)
        .eq('status', 'pending')
      
      if (error) throw error
      
      toast.success('All suggestions dismissed')
      setShowSuggestionsBanner(false)
      setPendingSuggestions([])
    } catch (err) {
      console.error('Failed to dismiss suggestions:', err)
      toast.error('Failed to dismiss suggestions')
    }
  }

  const handleOpenForm = (item = null) => {
    if (item && team.season_tier === 'free') {
      alert('Editing wiki items is a Pro feature. Upgrade to access full wiki management.')
      return
    }

    if (item) {
      setEditingItem(item)
      setFormData({
        title: item.title,
        content: item.content,
        type: item.type || 'note',
        section: item.section || 'Quick Notes',
        parent_id: item.parent_id || item.parent_item_id || null, // Support both for compatibility
        allow_ai_override: item.allow_ai_override || false
      })
      // Load parent items for this section
      loadParentItemsForSection(item.section || 'Quick Notes', item.id)
    } else {
      setEditingItem(null)
      setFormData({
        title: '',
        content: '',
        type: 'note',
        section: 'Quick Notes',
        parent_id: null,
        allow_ai_override: false
      })
      loadParentItemsForSection('Quick Notes')
    }
    setShowForm(true)
  }

  const loadParentItemsForSection = (section, excludeId = null) => {
    const parents = wikiItems.filter(item => 
      item.section === section && 
      !item.parent_id && !item.parent_item_id && // Support both for compatibility
      item.id !== excludeId
    )
    setParentItemsInSection(parents)
  }

  const handleSectionChange = (newSection) => {
    setFormData(prev => ({
      ...prev,
      section: newSection,
      parent_id: null // Reset parent when section changes
    }))
    loadParentItemsForSection(newSection, editingItem?.id)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingItem(null)
    setFormData({
      title: '',
      content: '',
      type: 'note',
      section: 'Quick Notes',
      parent_id: null,
      allow_ai_override: false
    })
    setParentItemsInSection([])
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.title.trim() || !formData.content.trim()) return

    setSaving(true)

    try {
      if (editingItem) {
        // Update existing item (paid tier only)
        if (team.season_tier === 'free') {
          alert('Editing wiki items is a Pro feature. Upgrade to access full wiki management.')
          setSaving(false)
          return
        }

        const { data, error} = await supabase
          .from('wiki_items')
          .update({
            title: formData.title.trim(),
            content: formData.content.trim(),
            type: formData.type || 'note',
            section: formData.section || 'Quick Notes',
            parent_id: formData.parent_id || null,
            allow_ai_override: formData.allow_ai_override || false,
            source: 'manual'
          })
          .eq('id', editingItem.id)
          .eq('team_id', team.id)
          .select()
          .single()

        if (error) throw error

        setWikiItems(wikiItems.map(item => 
          item.id === editingItem.id ? data : item
        ))
      } else {
        // Create new item
        const { data, error } = await supabase
          .from('wiki_items')
          .insert({
            team_id: team.id,
            title: formData.title.trim(),
            content: formData.content.trim(),
            type: formData.type || 'note',
            section: formData.section || 'Quick Notes',
            parent_id: formData.parent_id || null,
            source: 'manual'
          })
          .select()
          .single()

        if (error) throw error

        setWikiItems([data, ...wikiItems])
      }

      handleCloseForm()
    } catch (err) {
      alert('Failed to save wiki item')
    } finally {
      setSaving(false)
    }
  }

  const handleTogglePin = async (itemId, currentPinned) => {
    // Paid tier only
    if (team.season_tier === 'free') {
      alert('Pinning wiki items is a Pro feature.')
      return
    }


    try {
      const { error } = await supabase
        .from('wiki_items')
        .update({ is_pinned: !currentPinned })
        .eq('id', itemId)
        .eq('team_id', team.id)

      if (error) throw error

      setWikiItems(wikiItems.map(item => 
        item.id === itemId ? { ...item, is_pinned: !currentPinned } : item
      ))
    } catch (err) {
      toast.error('Failed to update wiki item')
    }
  }

  const handleDeleteItem = async (itemId) => {
    // Paid tier only
    if (team.season_tier === 'free') {
      toast.error('Deleting wiki items is a Pro feature.')
      return
    }

    // Check for child items that would be deleted (CASCADE)
    const childItems = wikiItems.filter(item => item.parent_id === itemId || item.parent_item_id === itemId)
    const itemToDelete = wikiItems.find(item => item.id === itemId)
    
    let confirmMessage = `Delete "${itemToDelete?.title || 'this wiki item'}"?`
    if (childItems.length > 0) {
      confirmMessage = `Delete "${itemToDelete?.title}"?\n\n⚠️ This will also delete ${childItems.length} child item${childItems.length === 1 ? '' : 's'}:\n${childItems.slice(0, 3).map(c => `• ${c.title}`).join('\n')}${childItems.length > 3 ? `\n• ...and ${childItems.length - 3} more` : ''}`
    }

    const confirmed = await confirmDialog({
      title: childItems.length > 0 ? 'Delete Parent and Children?' : 'Delete Wiki Item?',
      message: confirmMessage,
      confirmText: childItems.length > 0 ? `Delete All (${childItems.length + 1} items)` : 'Delete',
      confirmVariant: 'danger'
    })
    if (!confirmed) return

    try {
      const { error } = await supabase
        .from('wiki_items')
        .delete()
        .eq('id', itemId)
        .eq('team_id', team.id)

      if (error) throw error

      // Remove deleted item and its children from local state
      const deletedIds = new Set([itemId, ...childItems.map(c => c.id)])
      setWikiItems(wikiItems.filter(item => !deletedIds.has(item.id)))
      
      toast.success(childItems.length > 0 
        ? `Deleted ${childItems.length + 1} items` 
        : 'Wiki item deleted'
      )
    } catch (err) {
      toast.error('Failed to delete wiki item')
    }
  }

  const getTypeColor = (type) => {
    const colors = {
      link: 'bg-blue-100 text-blue-800',
      address: 'bg-green-100 text-green-800',
      phone: 'bg-purple-100 text-purple-800',
      note: 'bg-gray-100 text-gray-800',
      document: 'bg-amber-100 text-amber-800',
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  if (teamLoading || loading) {
    return <Loading text="Loading wiki..." />
  }

  if (!team) {
    navigate('/team-setup')
    return null
  }

  const isFree = team.season_tier === 'free'

  return (
    <div className="min-h-screen bg-brand-cream py-8">
      <Container>
        <div className="max-w-4xl mx-auto">
          {/* Navigation Bar */}
          <div className="mb-6">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="secondary"
              className="text-sm"
            >
              ← Back to Dashboard
            </Button>
          </div>

          <Card>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h1 className="font-heading text-3xl font-bold mb-2">
                  Team Wiki
                </h1>
                <p className="text-brand-slate">
                  {isFree 
                    ? 'Basic wiki access - Upgrade to Pro for full management' 
                    : 'Quick reference information for your team'}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  variant="secondary"
                  onClick={() => setShowVersionHistory(true)}
                  className="text-sm"
                  disabled={loading}
                >
                  📜 History
                </Button>
                {!isFree && (
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/wiki/edit')}
                    className="text-sm"
                    disabled={loading}
                  >
                    ✏️ Edit Wiki
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={() => handleOpenForm()}
                  disabled={loading}
                >
                  + Add Item
                </Button>
              </div>
            </div>

            {/* Auto-Update Success Notice (passive, dismissible) */}
            {showAutoUpdateNotice && recentAutoUpdates.length > 0 && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 text-xl">✓</span>
                    <div>
                      <p className="font-medium text-green-900">
                        {(() => {
                          const creates = recentAutoUpdates.filter(u => u.operation === 'create').length
                          const updates = recentAutoUpdates.filter(u => u.operation === 'update').length
                          if (creates > 0 && updates > 0) {
                            return `Wiki updated: ${creates} added, ${updates} modified`
                          } else if (updates > 0) {
                            return `Wiki updated: ${updates} item${updates === 1 ? '' : 's'} modified`
                          } else {
                            return `Wiki updated with ${creates} new item${creates === 1 ? '' : 's'}`
                          }
                        })()}
                      </p>
                      <p className="text-sm text-green-700">
                        {recentAutoUpdates.slice(0, 3).map(u => u.new_data?.title).filter(Boolean).join(', ')}
                        {recentAutoUpdates.length > 3 && ` +${recentAutoUpdates.length - 3} more`}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => setShowRecentChanges(true)}
                      className="text-sm"
                    >
                      View Changes
                    </Button>
                    <button
                      onClick={() => setShowAutoUpdateNotice(false)}
                      className="text-green-600 hover:text-green-800 text-xl"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Queued Items Notice (only for edge cases needing review) */}
            {showSuggestionsBanner && pendingSuggestions.length > 0 && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-amber-900">
                    <span className="font-medium">{pendingSuggestions.length}</span> {pendingSuggestions.length === 1 ? 'item needs' : 'items need'} your review
                    <span className="text-amber-700 ml-2">(lower confidence or updates to existing items)</span>
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => navigate('/wiki/review')}
                      className="text-sm"
                    >
                      Review
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleDismissAll}
                      className="text-sm text-amber-700"
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* View Toggle and Search */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-4 border-b border-border">
              <WikiViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
              <input
                type="text"
                placeholder="Search wiki..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green w-full sm:w-64"
              />
            </div>

            {loading ? (
              <div className="text-center py-8 text-brand-slate">
                Loading wiki items...
              </div>
            ) : wikiItems.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-brand-slate mb-4">
                  No wiki items yet.
                </p>
                <Button
                  variant="primary"
                  onClick={() => handleOpenForm()}
                >
                  Add Your First Item
                </Button>
              </div>
            ) : viewMode === 'document' ? (
              <WikiDocumentView wikiItems={wikiItems} searchQuery={searchQuery} teamId={team.id} />
            ) : (
              <div className="space-y-3">
                {wikiItems.map((item) => (
                  <div
                    key={item.id}
                    className={`p-4 border-2 rounded-lg ${
                      item.is_pinned 
                        ? 'border-amber-300 bg-amber-50' 
                        : 'border-border bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-medium text-brand-charcoal">
                            {item.title}
                          </h3>
                          <span className={`text-xs px-2 py-0.5 rounded ${getTypeColor(item.type)}`}>
                            {item.type}
                          </span>
                          {item.is_pinned && (
                            <Badge variant="warning">Pinned</Badge>
                          )}
                          {item.source === 'ai_suggested' && (
                            <Badge variant="blue">AI</Badge>
                          )}
                        </div>
                        <p className="text-sm text-brand-slate break-all">
                          {item.type === 'link' ? (
                            <a 
                              href={item.content} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-brand-green hover:underline"
                            >
                              {item.content}
                            </a>
                          ) : (
                            item.content
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!isFree && (
                          <>
                            <button
                              onClick={() => handleTogglePin(item.id, item.is_pinned)}
                              className="text-sm text-brand-slate hover:text-brand-charcoal"
                              title={item.is_pinned ? 'Unpin' : 'Pin'}
                            >
                              📌
                            </button>
                            <Button
                              variant="secondary"
                              onClick={() => handleOpenForm(item)}
                              className="text-sm px-3 py-1.5"
                            >
                              Edit
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={() => handleDeleteItem(item.id)}
                              className="text-sm px-3 py-1.5 text-red-600 border-red-300 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isFree && (
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-900">
                  <strong>Free Tier:</strong> You can add wiki items, but editing, deleting, and pinning are Pro features. <a href="/pricing" className="underline">Upgrade to Pro</a> for full wiki management.
                </p>
              </div>
            )}
          </Card>

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <Card className="max-w-2xl w-full">
                <h2 className="font-heading text-2xl font-bold mb-4">
                  {editingItem ? 'Edit Wiki Item' : 'Add Wiki Item'}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    label="Title"
                    placeholder="e.g., Tournament Hotel, Coach Phone, Practice Location"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    disabled={saving}
                  />

                  <Select
                    label="Type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    disabled={saving}
                  >
                    <option value="note">Note</option>
                    <option value="link">Link</option>
                    <option value="address">Address</option>
                    <option value="phone">Phone</option>
                    <option value="document">Document Link</option>
                  </Select>

                  <Select
                    label="Section"
                    value={formData.section}
                    onChange={(e) => handleSectionChange(e.target.value)}
                    disabled={saving}
                    options={[
                      { value: 'Tournaments', label: '🏆 Tournaments' },
                      { value: 'Team Contacts', label: '📞 Team Contacts' },
                      { value: 'Policies & Rules', label: '📋 Policies & Rules' },
                      { value: 'Logistics', label: '📍 Logistics' },
                      { value: 'Training & Resources', label: '📚 Training & Resources' },
                      { value: 'Quick Notes', label: '📝 Quick Notes' }
                    ]}
                  />

                  {['Tournaments'].includes(formData.section) && (
                    <Select
                      label="Group Under (Optional)"
                      value={formData.parent_id || ''}
                      onChange={(e) => setFormData({ ...formData, parent_id: e.target.value || null })}
                      disabled={saving}
                      options={[
                        { value: '', label: 'None (create new group)' },
                        ...parentItemsInSection.map(item => ({
                          value: item.id,
                          label: item.title
                        }))
                      ]}
                    />
                  )}

                  <Textarea
                    label={formData.type === 'document' ? 'Document URL' : 'Content'}
                    placeholder={
                      formData.type === 'link' 
                        ? 'https://...' 
                        : formData.type === 'phone'
                        ? '555-123-4567'
                        : formData.type === 'address'
                        ? '123 Main St, City, State ZIP'
                        : formData.type === 'document'
                        ? 'https://docs.google.com/... or https://drive.google.com/...'
                        : 'Enter your information here...'
                    }
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                    disabled={saving}
                    rows={formData.type === 'document' ? 1 : 3}
                  />
                  {formData.type === 'document' && (
                    <p className="text-xs text-brand-slate -mt-2">
                      Paste a link to your document (Google Doc, PDF, Dropbox, etc.)
                    </p>
                  )}

                  {/* Allow AI Override Toggle - shown when editing items that were manually edited */}
                  {editingItem && editingItem.manually_edited_at && (
                    <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div>
                        <p className="font-medium text-blue-900 text-sm">Allow AI Updates</p>
                        <p className="text-xs text-blue-700 mt-0.5">
                          This item is protected from AI changes because you edited it manually. 
                          Enable this to let AI update it on your next dispatch.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-4">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={formData.allow_ai_override}
                          onChange={(e) => setFormData({ ...formData, allow_ai_override: e.target.checked })}
                          disabled={saving}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleCloseForm}
                      disabled={saving}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={saving || !formData.title.trim() || !formData.content.trim()}
                      className="flex-1"
                    >
                      {saving ? 'Saving...' : (editingItem ? 'Update' : 'Add')}
                    </Button>
                  </div>
                </form>
              </Card>
            </div>
          )}

          {/* Version History Modal */}
          {showVersionHistory && (
            <WikiVersionHistory
              teamId={team?.id}
              onRollback={() => {
                fetchWikiItems()
                setShowVersionHistory(false)
              }}
              onClose={() => setShowVersionHistory(false)}
            />
          )}

          {/* Recent Changes Modal */}
          {showRecentChanges && (
            <WikiRecentChanges
              teamId={team?.id}
              onClose={() => setShowRecentChanges(false)}
              onUndo={() => {
                fetchWikiItems()
                fetchRecentAutoUpdates()
              }}
            />
          )}
        </div>
      </Container>
    </div>
  )
}

export default WikiManager


